import api from '../utils/axiosConfig';
import { IOCEntry, HuntResponse } from '../types';

interface GetIOCsParams {
  client_id?: string;
  ioc_type?: string;
  is_blocked?: boolean;
  is_malicious?: boolean;
  source?: string;
  min_score?: number;
  page?: number;
  per_page?: number;
}

export interface IngestIOCPayload {
  client_id: string;
  ioc_value: string;
  ioc_type: string;
  notes?: string;
  tags?: string[];
  block?: boolean;
}

export class IOCsService {
  static async getIOCs(params?: GetIOCsParams): Promise<IOCEntry[]> {
    try {
      const response = await api.get('/iocs', { params });
      return response.data.iocs || [];
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to fetch IOCs');
    }
  }

  static async ingestIOC(payload: IngestIOCPayload): Promise<IOCEntry> {
    try {
      const response = await api.post('/iocs', payload);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to ingest IOC');
    }
  }

  static async ingestBulk(clientId: string, csvData: string): Promise<{ queued: number; invalid: any[] }> {
    try {
      const response = await api.post('/iocs/bulk', { client_id: clientId, csv_data: csvData });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to bulk ingest IOCs');
    }
  }

  static async blockIOC(id: string, blockedUntil?: string | null): Promise<IOCEntry> {
    try {
      const response = await api.patch(`/iocs/${id}`, {
        is_blocked: true,
        ...(blockedUntil !== undefined ? { blocked_until: blockedUntil } : {}),
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to block IOC');
    }
  }

  static async unblockIOC(id: string): Promise<IOCEntry> {
    try {
      const response = await api.patch(`/iocs/${id}`, { is_blocked: false });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to unblock IOC');
    }
  }

  static async updateIOC(id: string, data: { notes?: string; tags?: string[] }): Promise<IOCEntry> {
    try {
      const response = await api.patch(`/iocs/${id}`, data);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to update IOC');
    }
  }

  static async deleteIOC(id: string): Promise<void> {
    try {
      await api.delete(`/iocs/${id}`);
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to delete IOC');
    }
  }

  static async getHuntResults(iocId: string): Promise<HuntResponse> {
    try {
      const response = await api.get(`/iocs/${iocId}/hunt`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to run hunt');
    }
  }

  static async bulkAction(payload: {
    action: 'block' | 'unblock' | 'delete';
    ids: string[];
    blocked_until?: string;
  }): Promise<{ success: number; failed: Array<{ id: string; reason: string }> }> {
    try {
      const response = await api.post('/iocs/bulk-action', payload);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to perform bulk action');
    }
  }

  static getExportUrl(clientId: string, params?: { is_blocked?: boolean; is_malicious?: boolean }): string {
    const base = api.defaults.baseURL || '';
    const qs = new URLSearchParams({ client_id: clientId, ...Object.fromEntries(
      Object.entries(params || {}).filter(([, v]) => v !== undefined).map(([k, v]) => [k, String(v)])
    )}).toString();
    return `${base}/iocs/export?${qs}`;
  }
}
